import { ArrowLeft, Mic, Square } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { t } from '../../i18n';
import { AudioRecorder } from '../../recorder';
import type { PrayerTime, RecordIntent, TodayResponse } from '../../types';
import { useToast } from '../shared/Toast';

const PRAYER_NAME_KEY: Record<PrayerTime, string> = {
  bomdod: 'prayer.bomdod',
  peshin: 'prayer.peshin',
  asr: 'prayer.asr',
  shom: 'prayer.shom',
  xufton: 'prayer.xufton',
};

const BAR_COUNT = 22;

function fmtTimer(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function Recorder() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const intent = ((location.state as RecordIntent | null) ?? { mode: 'recitation' }) as RecordIntent;

  const recorderRef = useRef<AudioRecorder | null>(null);
  const ampBufferRef = useRef<number[]>(Array.from({ length: BAR_COUNT }, () => 0.05));
  const [, forceRender] = useState(0);
  const [today, setToday] = useState<TodayResponse | null>(null);
  const [state, setState] = useState<'idle' | 'recording' | 'finishing'>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.today().then((d) => {
      setToday(d);
      if (intent.mode === 'recitation' && !d.current_prayer) {
        toast.show(t('dashboard.day_done'), 'info');
        navigate('/child', { replace: true });
        return;
      }
      if (intent.mode === 'memorization') {
        const slot = d.memorization?.slots.find((s) => s.time === intent.slot);
        if (!d.memorization || !d.memorization.is_active || !slot) {
          navigate('/child', { replace: true });
        }
      }
    });
    return () => {
      recorderRef.current?.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, toast]);

  if (!today) return null;

  // Resolve the recording context for the active module.
  let contextLabel = '';
  let surah: { number: number; name_uz: string; name_ar: string } | null = null;
  if (intent.mode === 'recitation') {
    if (!today.current_prayer) return null;
    const row = today.prayers.find((p) => p.prayer_time === today.current_prayer);
    const rep = (row?.recitation_count ?? 0) + 1;
    contextLabel = t('record.context', {
      prayer: t(PRAYER_NAME_KEY[today.current_prayer]),
      surah: today.current_surah.name_uz,
      n: rep,
    });
    surah = today.current_surah;
  } else {
    const mem = today.memorization;
    const slot = mem?.slots.find((s) => s.time === intent.slot);
    if (!mem || !slot) return null;
    const rep = slot.count + 1;
    contextLabel = t('record.context.mem', {
      time: slot.time,
      surah: mem.surah.name_uz,
      from: mem.ayah_from,
      to: mem.ayah_to,
      n: rep,
    });
    surah = mem.surah;
  }

  const start = async () => {
    setError(null);
    const rec = new AudioRecorder({
      onAmplitude: (rms) => {
        const buf = ampBufferRef.current;
        buf.shift();
        buf.push(Math.min(1, rms * 5));
        forceRender((n) => n + 1);
      },
      onTimeUpdate: (s) => setElapsed(s),
      onMaxDuration: () => toast.show(t('common.error.max_duration'), 'info'),
      onError: (err) => setError(err.message),
    });
    recorderRef.current = rec;
    try {
      await rec.start();
      setState('recording');
    } catch (err) {
      const msg = err instanceof Error && err.name === 'NotAllowedError'
        ? t('common.error.permission_mic')
        : (err instanceof Error ? err.message : t('common.error.network'));
      setError(msg);
      setState('idle');
    }
  };

  const stop = async () => {
    if (!recorderRef.current) return;
    setState('finishing');
    try {
      const result = await recorderRef.current.stop();
      const base = {
        blob: result.blob,
        blob_url: URL.createObjectURL(result.blob),
        duration_seconds: result.duration_seconds,
        mime: result.mime,
        ext: result.ext,
        intent,
        surah,
      };
      const reviewState =
        intent.mode === 'memorization'
          ? {
              ...base,
              module: 'memorization' as const,
              slot: intent.slot,
              ayah_from: today.memorization?.ayah_from ?? 1,
              ayah_to: today.memorization?.ayah_to ?? 1,
            }
          : {
              ...base,
              module: 'recitation' as const,
              prayer_time: today.current_prayer,
            };
      navigate('/child/review', { state: reviewState });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'recording_failed');
      setState('idle');
    } finally {
      recorderRef.current = null;
    }
  };

  return (
    <div className="min-h-dvh bg-record-bg text-record-fg font-sans">
      <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-between p-6">
        <header className="relative w-full text-center text-[11px] uppercase tracking-wide text-record-fg/70">
          {state === 'idle' ? (
            <button
              onClick={() => navigate('/child', { replace: true })}
              aria-label={t('common.back')}
              className="absolute left-0 top-1/2 -translate-y-1/2 text-record-fg/70 active:scale-[0.97]"
            >
              <ArrowLeft size={20} />
            </button>
          ) : null}
          <span>{contextLabel}</span>
        </header>

        <section className="flex flex-col items-center gap-8">
          <div className="font-mono text-[44px] font-medium tabular-nums">
            {fmtTimer(elapsed)}
          </div>
          <Waveform amps={ampBufferRef.current} active={state === 'recording'} />
          <button
            type="button"
            disabled={state === 'finishing'}
            onClick={state === 'recording' ? stop : start}
            aria-label={state === 'recording' ? t('record.stop') : t('record.start')}
            className={[
              'flex h-[120px] w-[120px] items-center justify-center rounded-full transition active:scale-[0.97]',
              state === 'recording'
                ? 'bg-danger shadow-[0_0_0_4px_rgba(226,75,74,0.25)]'
                : 'bg-record-fg/15 ring-1 ring-record-fg/20',
            ].join(' ')}
          >
            {state === 'recording' ? (
              <Square fill="currentColor" size={36} className="text-record-fg" />
            ) : (
              <Mic size={40} className="text-record-fg" />
            )}
          </button>
          <p className="text-[13px] text-record-fg/70">
            {state === 'recording' ? t('record.stop') : t('record.start')}
          </p>
        </section>

        <footer className="flex flex-col items-center gap-1 text-center text-[11px] text-record-fg/55">
          {intent.mode === 'recitation' ? (
            <span>{t('record.rule_footer', { rule: today.daily_rule_text })}</span>
          ) : null}
          {state === 'recording' ? <span>{t('record.mic_active')}</span> : null}
          {error ? <span className="text-danger">{error}</span> : null}
        </footer>
      </main>
    </div>
  );
}

function Waveform({ amps, active }: { amps: number[]; active: boolean }) {
  return (
    <div className="flex h-[40px] items-end gap-[3px]">
      {amps.map((a, i) => (
        <span
          key={i}
          className={[
            'inline-block w-[3px] rounded-sm bg-record-accent transition-[height] duration-75',
          ].join(' ')}
          style={{
            height: active ? `${Math.max(4, Math.min(40, a * 40))}px` : '6px',
            opacity: active ? 0.9 : 0.4,
          }}
        />
      ))}
    </div>
  );
}
